import { put as sagaPut, call, select } from "redux-saga/effects";
import api from "../utils/axios";
import { REFRESH_ACCESS_TOKEN_ENPOINT } from "./endpoints";
import * as actions from "../containers/Authentication/redux/actions";
import { setSessionExpired, showError } from "../globalRedux/actions";
import { store } from "../store";
import { API_URL } from "../utils/axios";
import axios from "axios";
import { checkBlacklistEndPoint } from "./functions/endpointBlacklistAuth";
import { addAuthorizationHeader } from "./performAuthorized";
import isWithinTimeframe from "./functions/apiRequestTimestamp";
import { checkResponse, logError, responseError } from "./apiChecking";
import { getSessionStorage, setSessionStorage, removeSessionStorage, getSessionStorageSync, setSessionStorageSync } from "./session";
import {
  CS_00031,
  CS_00058,
  CS_00071,
  CTO_00001,
  ETN_00002,
  OE_00005,
  OE_00006,
  POS_00001,
  TA_504,
} from "./errorCodes";
import { getSessionMasterId } from "./functions/getSessionValues";

let isRefreshing = false;
let failedQueue = [];

function decrypt(encryptedText, key) {
  let result = "";
  encryptedText = atob(encryptedText);
  for (let i = 0; i < encryptedText.length; i++) {
    result += String.fromCharCode(
      encryptedText.charCodeAt(i) ^ key.charCodeAt(i % key.length)
    );
  }
  return result;
}

const getSafeMobileNo = encryptedMobileNo => {
  if (typeof encryptedMobileNo !== "string" || encryptedMobileNo.trim() === "") {
    return null;
  }

  try {
    return decrypt(encryptedMobileNo, "xTokenX");
  } catch (_) {
    // Keep compatibility for sessions where mobileNo may be stored as plain text.
    return /^\+?\d{6,}$/.test(encryptedMobileNo) ? encryptedMobileNo : null;
  }
};

const getRenewPayload = async () => {
  let tokenRenew = await getSessionStorage("mpAccessToken");
  let encryptedMobileNo = await getSessionStorage("renew");

  const mobileNo = getSafeMobileNo(encryptedMobileNo);

  return {
    Token: tokenRenew,
    mobileNo,
  };
};
const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

const getMpUserInfo = async () => {
  let masterId = await getSessionStorage("masterId");
  if (!masterId) {
    return {};
  }
  return { masterId };
};

const getRefreshToken = state => state.auth.refreshToken;

/**
 * * send log error to backend
 * * START
 */
export const sendLogError = async payload => {
  let infoUser = await getMpUserInfo();
  payload.fullName = infoUser.fullName || "Not Found";
  payload.masterId = infoUser.masterId || "Not Found";
  payload.mobileNo = infoUser.mobileNo || "Not Found";
  const res = await api.post(`${API_URL.mp}/logger/log`, payload);
  // console.log("log success");
};
/**
 * * send log error to backend
 * * END
 */

function* setShowError(error) {
  let errorMessageDefault =
    "We are facing some technical issues, and we are working very hard to resolve the issue.";

  if (typeof error === "object" && "data" in error && "message" in error.data) {
    if (
      !process.env.VITEST &&
      process.env.REACT_APP_ENV.trim() !== "production"
    ) {
      errorMessageDefault = error.data.message;
    }
  }
  yield sagaPut(showError(errorMessageDefault));
}

function* setSessionExpiredError() {
  yield sagaPut(setSessionExpired());
}

// A session is considered expired only when the token-refresh call itself is
// rejected as a bad request; every other failure is a normal API error.
const checkSessionExpired = (error) => {
  console.log("renewToken: ", error?.config?.url === `${API_URL.mp}/authorizations/renewtoken`);
  console.log("renew token errorCode: ", error?.code);
  return (
    error?.config?.url === `${API_URL.mp}/authorizations/renewtoken` &&
    error?.code === "ERR_BAD_REQUEST"
  );
}

const setLastRequestTimestamp = payload => ({
  type: "SET_LAST_REQUEST",
  payload,
});

api.interceptors.request.use(
  async config => {
    const controller = new AbortController();

    if (
      window.hasOwnProperty("TOKEN_ERROR") &&
      window["TOKEN_ERROR"] &&
      config?.url?.includes("logger")
    ) {
      console.log(`aborting ${config.url}...`);
      controller.abort();
      config.aborted = controller.signal.aborted;
      throw config;
    }

    let bearerToken = await getSessionStorage("mpBearerToken");

    if (bearerToken && checkBlacklistEndPoint(config?.url)) {
      config.headers.Authorization = `Bearer ${bearerToken}`;
    }

    config.signal = controller.signal;

    config.metadata = { startTime: new Date() };

    // Persist a deeplink/plan marker at request time. A later session-expiry
    // can fire after the app has already navigated to "/", so the live path is
    // unreliable for detecting the deeplink flow in the token-refresh handler.
    const requestPath = window.location?.pathname || "";
    if (requestPath.includes("/deeplink") || requestPath.includes("/plan")) {
      setSessionStorageSync("deeplinkFlow", "1");
      // Remember the most recent deeplink/plan page so we can resume there
      // after the session is restored.
      setSessionStorageSync("deeplinkLastPath", requestPath);
    }

    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

const networkErrors = [];

window.addEventListener("online", () => {
  for (let i = networkErrors.length - 1; i >= 0; i--) {
    const error = networkErrors[i];
    window.log({
      path: error?.config?.url,
      status: "408",
      res: error?.message,
      req: {
        params: error?.config?.params,
        body: error?.config?.data || error?.request?.body,
      },
      requestDate: error?.config?.metadata?.startTime,
      responseDate: error?.config?.metadata?.endTime,
      responseTime: `${error?.duration}ms`,
    });
    networkErrors.splice(i);
  }
});

api.interceptors.response.use(
  response => {
    let responseResult = checkResponse(response);
    if (response.config?.metadata) {
      response.config.metadata.endTime = new Date();
    }
    response.duration =
      response.config?.metadata?.endTime - response.config?.metadata?.startTime;

    if (!responseResult || response.status === 204) {
      responseError(response);
      throw new Error("API Response is empty");
    }
    return response;
  },
  async error => {
    if (error.config?.metadata) {
      error.config.metadata.endTime = new Date();
    }
    const originalRequest = error.config;
    error.duration =
      error.config?.metadata?.endTime - error.config?.metadata?.startTime;

    handleNetworkError(error);

    if (!error.config?.url.includes("logger") && !window["TOKEN_ERROR"]) {
      logError(error);
    }

    if (error.response) {
      let payload = {
        api: error.config?.url || window.location.pathname,
        message: error.response?.data?.message || "---",
        errorResponse: true,
        status: error.response?.status || "Not Found",
      };
      if (error.config?.url !== `${API_URL.mp}/logger/log`) {
        sendLogError(payload);
      }

      if (error.response.status === 401 && !originalRequest._retry) {
        window["TOKEN_ERROR"] = true;

        if (isRefreshing) {
          return new Promise(function (resolve, reject) {
            failedQueue.push({ resolve, reject });
          })
            .then(token => {
              originalRequest.headers["Authorization"] = "Bearer " + token;
              return axios(originalRequest);
            })
            .catch(err => {
              return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        const refreshToken = await getRenewPayload()
          .then(data => data)
          .catch(err => {
            console.log("err: ", err);

            return {
              Token: null,
              mobileNo: null,
            };
          });

        return new Promise(function (resolve, reject) {
          axios
            .post(`${API_URL.mp}/authorizations/renewtoken`, refreshToken)
            .then(({ data }) => {
              localStorage.setItem("reqTokenDate", Date.now());
              setSessionStorage("mpBearerToken", data.bearerToken);
              originalRequest.headers["Authorization"] =
                "Bearer " + data.bearerToken;

              processQueue(null, data.bearerToken);
              resolve(axios(originalRequest));
            })
            .catch(err => {
              if (
                err.config &&
                err.config?.url === `${API_URL.mp}/authorizations/renewtoken`
              ) {
                sendLogError({
                  api:
                    err.config?.url ||
                    `${API_URL.mp}/authorizations/renewtoken`,
                  message: err.message || "",
                });
              }
              processQueue(err, null);
              // remove localstorage if fail renewtoken
              [
                "mpBearerToken",
                "mpAccessToken",
              ].forEach(key => removeSessionStorage(key));
              // For the session-expired case (renewtoken -> ERR_BAD_REQUEST)
              // on a deeplink/plan flow, skip the legacy `invalid_token`
              // teardown. That teardown clears storage and redirects to Get
              // Started (`history.push("/")`), which wipes the deeplink route
              // and its params. Instead we let the session-expired popup drive
              // re-auth so the customer resumes on the deeplink cash-in screen.
              //
              // `deeplinkFlow` is checked as a fallback because a deeplink can
              // complete onto a plain /dashboard route, where the live path no
              // longer contains "/deeplink".
              const currentPath = window.location.pathname;
              const isDeeplinkFlow =
                currentPath.includes("/deeplink") ||
                currentPath.includes("/plan") ||
                getSessionStorageSync("deeplinkFlow") === "1";

              const isSessionExpired = checkSessionExpired(err);
              if((isSessionExpired && !isDeeplinkFlow)) {
                store.dispatch(setSessionExpired());
              } else if((isSessionExpired && isDeeplinkFlow)) {
                // Flag that, once the session is silently restored, we should
                // resume on the remembered deeplink page instead of the
                // default dashboard routing.
                setSessionStorageSync("deeplinkResumePending", "1");
              } else {
                window.eventEmitter.emit("invalid_token");
              }
              reject(err);
            })
            .finally(() => {
              isRefreshing = false;
              window["TOKEN_ERROR"] = false;
            });
        });
      }

      return Promise.reject(error.response);
    }
  }
);

export function* get(endpoint, options, statePath) {
  try {
    let token =
      (yield call(getSessionStorage, "mpBearerToken")) ||
      (yield select(state => state.auth.MPtoken));

    const config = addAuthorizationHeader(options, token);
    if (statePath) {
      const cacheExpired = yield call(isWithinTimeframe, Date.now(), endpoint);

      if (!cacheExpired) {
        const res = yield call(api.get, endpoint, config || {});
        yield sagaPut(
          setLastRequestTimestamp({
            timestamp: Date.now(),
            key: endpoint,
          })
        );
        return res;
      }

      const [reducer, data] = statePath.split(".");

      return yield select(state => ({
        data: state[reducer][data],
      }));
    } else {
      return yield call(api.get, endpoint, config || {});
    }
  } catch (error) {
    if (checkSessionExpired(error)) {
      return yield call(setSessionExpiredError);
    }
    if (
      error?.data?.errorCode === POS_00001 ||
      endpoint.includes("/search-tin?")
    ) {
      return yield call(checkForTokenError, error, retryRequest, "accessToken");
    }

    yield call(setShowError, error);
    return yield call(checkForTokenError, error, retryRequest, "accessToken");
  }
}

export function* post(endpoint, data, options) {
  try {
    let token = yield select(state => state.auth.MPtoken);
    const config = addAuthorizationHeader(options, token);
    return yield call(api.post, endpoint, data, config || {});
  } catch (error) {
    if (checkSessionExpired(error)) {
      return yield call(setSessionExpiredError);
    }
    /**
     * Info: The error codes listed below are handled specifically to avoid showing generic error prompts.
     * - CTO-00001: Specific error related to summary transfer/switching.
     * - CS-00071: Age requirement prompt for retirement goals.
     * - CS-00031: Account verification error.
     * - ETN-00002: Specific error related to electronic transactions.
     * - 429: Too many requests error.
     * Additionally, if the endpoint includes "/transfer", it is also excluded from generic error handling.
     */
    if (
      error?.errorCode === CTO_00001 ||
      error?.data?.errorCode === CS_00071 ||
      error?.data?.errorCode === CS_00031 ||
      error?.data?.errorCode === ETN_00002 ||
      error?.status === 429 ||
      endpoint.includes("/transfer")
    ) {
      return yield call(checkForTokenError, error, retryRequest, "accessToken");
    }

    yield call(setShowError, error);
    return yield call(checkForTokenError, error, retryRequest, "accessToken");
  }
}

export function* put(endpoint, data, options) {
  try {
    let token = yield select(state => state.auth.MPtoken);
    const config = addAuthorizationHeader(options, token);
    return yield call(api.put, endpoint, data, config || {});
  } catch (error) {
    if (checkSessionExpired(error)) {
      return yield call(setSessionExpiredError);
    }
    yield call(setShowError, error);
    return yield call(checkForTokenError, error, retryRequest, "accessToken");
  }
}

export function* patch(endpoint, data, options) {
  try {
    let token = yield select(state => state.auth.MPtoken);
    const config = addAuthorizationHeader(options, token);
    return yield call(api.patch, endpoint, data, config || {});
  } catch (error) {
    if (checkSessionExpired(error)) {
      return yield call(setSessionExpiredError);
    }
    yield call(setShowError, error);
    return yield call(checkForTokenError, error, retryRequest, "accessToken");
  }
}

export function* del(endpoint, data, options) {
  try {
    let token = yield select(state => state.auth.MPtoken);
    const config = addAuthorizationHeader(options, token);
    return yield call(api.delete, endpoint, data, config || {});
  } catch (error) {
    if (checkSessionExpired(error)) {
      return yield call(setSessionExpiredError);
    }
    yield call(setShowError, error);
    return yield call(checkForTokenError, error, retryRequest, "accessToken");
  }
}

function* retryRequest(error) {
  const newToken = yield call(refreshAccessToken);
  const newError = { ...error };
  newError.config.headers.Authorization = `Bearer ${newToken}`;
  return yield call(api.request, newError.config);
}

function checkForTokenError(error, onAccessTokeErrorCallback, paramToCheck) {
  // if (error.response) {
  //   console.log(error.response);
  //   const accessTokenError = error.response.data.find(
  //     e => e.param === paramToCheck
  //   );
  //   if (accessTokenError) {
  //     return yield call(onAccessTokeErrorCallback, error);
  //   }
  //
  //   throw error.response;
  // } else if (error.request) {
  //   throw createSimpleErrorObject("Server does not respond");
  // }
  //
  // throw createSimpleErrorObject("Please, check your internet connection");
  throw createSimpleErrorObject(error);
}

function* refreshAccessToken() {
  try {
    const refreshToken = yield select(getRefreshToken);
    const response = yield call(api.post, REFRESH_ACCESS_TOKEN_ENPOINT, {
      refreshToken,
    });
    yield sagaPut(actions.accessTokenUpdatedAction(response.data));
    return response.data.accessToken;
  } catch (error) {
    return yield call(
      checkForTokenError,
      error,
      onRefreshTokenError,
      "refreshToken"
    );
  }
}

function* onRefreshTokenError(error) {
  yield sagaPut(actions.signOutAction());
  throw error;
}

function createSimpleErrorObject(error) {
  // - create retirement goal should show age requirement prompt error message
  // - /checkout endpoint error should be displayed if the code is CS-00058
  /**
   * Info: The error codes listed below are handled specifically to avoid showing generic error prompts.
   * - CS-00071: Age requirement prompt for retirement goals.
   * - CS-00058: Specific error related to checkout process.
   * - CS-00031: Account verification error.
   * - POS-00001: Specific error related to point of sale.
   * - CTO-00001: Specific error related to summary transfer/switching.
   * - ETN-00002: Specific error related to electronic transactions.
   * - 429: Too many requests error.
   * - OE-00005: Specific error related to duplicate order entry.
   * - TA-504: Specific error related to FNZ Timeout.
   */
  if (
    error?.data?.errorCode === CS_00071 ||
    error?.data?.errorCode === CS_00058 ||
    error?.data?.errorCode === CS_00031 ||
    error?.data?.errorCode === POS_00001 ||
    error?.data?.errorCode === CTO_00001 ||
    error?.data?.errorCode === ETN_00002 ||
    error?.status === 429 ||
    error?.data?.errorCode === OE_00005 ||
    // error?.data?.errorCode === OE_00006 ||
    error?.data?.errorCode === TA_504
  ) {
    return {
      ...error?.data,
      status: error?.status,
    };
  }

  if (
    error.code === "ECONNABORTED" ||
    (error.message && error.message.includes("timeout"))
  ) {
    return {
      message: "timeoutError",
    };
  }
  return {
    response: error?.response?.data,
  };
}

const handleNetworkError = error => {
  if (
    error instanceof Error &&
    "message" in error &&
    error.message.includes("Network Error")
  ) {
    networkErrors.push({
      config: error.config,
      message: error.message,
      duration: error.duration,
      request: error.request,
    });
  }
};

export const sendPersistLog = async payload => {
  try {
    let infoUser = getMpUserInfo();
    payload.fullName = infoUser.fullName || "Not Found";
    payload.masterId = infoUser.masterId || "Not Found";
    payload.mobileNo = infoUser.mobileNo || "Not Found";
    return await api.post(`${API_URL.mp}/logger/log`, payload);
  } catch (err) {
    return err;
  }
};
